import { CREATE_INQUIRY, type CreateInquiryQuery } from "@App/graphql/mutations/createInquiry";
import { GET_NUMBERPLATE_CHECK } from "@App/graphql/queries";
import { useContext } from "@App/hooks/useContext";
import type { State } from "@App/state";
import { selectInitialRegistrationDate } from "@App/state/selectors/orders";
import { OrganizationIsALD } from "@App/utils/conditionals/organization";
import { PremiumServiceIsRentalCar } from "@App/utils/conditionals/services";
import { formatDate, toIsoString } from "@App/utils/dates";
import { eligibleGarageProperties } from "@App/utils/garages/eligibleGaragePropertiesUtil";
import { useLazyQuery, useMutation } from "@apollo/client";
import type { FormEvent } from "react";
import { useSelector } from "react-redux";

export const useCreateInquiry = ({
  firstName,
  lastName,
  email,
  phone,
  street,
  city,
  postcode,
  addition,
  tos,
  marketing,
  adacMember,
  message,
  numberPlate,
  mileage,
  availableGarageProperties,
  additionalCosts,
  reviewOptIn,
}) => {
  const organization = useSelector((state: State) => state.orders.organization);
  const service = useSelector((state: State) => state.orders.service);
  const garage = useSelector((state: State) => state.orders.garage);
  const premiumServices = useSelector((state: State) => state.orders.premiumServices);
  const slot = useSelector((state: State) => state.orders.slot);
  const mustUseAuthorizedGarage = useSelector(
    (state: State) => state.orders.mustUseAuthorizedGarage,
  );
  const brand = useSelector((state: State) => state.orders.brand);
  const generation = useSelector((state: State) => state.orders.generation);
  const motorization = useSelector((state: State) => state.orders.motorization);
  const gearbox = useSelector((state: State) => state.orders.gearbox);
  const body = useSelector((state: State) => state.orders.body);
  const initialRegistrationDate = useSelector(selectInitialRegistrationDate);
  const subRepairs = useSelector((state: State) => state.orders.subRepairs);
  const vin = useSelector((state: State) => state.orders.vin);
  const hsnTsn = useSelector((state: State) => state.orders.hsntsn);
  const claimNumber = useSelector((state: State) => state.orders.claimNumber);
  const contractNumber = useSelector((state: State) => state.orders.contractNumber);

  const rentalCarSelection = useSelector((state: State) => state.orders.rentalCarSelection);
  const serviceWithAdditionalInformation = OrganizationIsALD(organization)
    ? premiumServices.find((premiumService) => PremiumServiceIsRentalCar(premiumService))
    : null;

  const [createInquiry, { loading, data, error }] = useMutation<CreateInquiryQuery>(CREATE_INQUIRY);

  const [checkNumberPlate] = useLazyQuery(GET_NUMBERPLATE_CHECK, {
    variables: {
      organization: organization.id,
      numberPlate: numberPlate || null,
      service: service?.id,
    },
  });

  const { getContext } = useContext();

  const submit = (e: FormEvent) => {
    e.preventDefault();
    createInquiry({
      variables: {
        organization: organization.id,
        services: service ? [service.id] : null,
        service: service ? service.id : null,
        garage: garage?.id ?? null,
        firstName: firstName,
        lastName: lastName,
        email: email,
        phone: phone.country + parseInt(phone.number, 10),
        street: street,
        city: city,
        postcode: postcode,
        addition: addition,
        numberPlate: numberPlate || null,
        mileage: parseInt(mileage, 10),
        message: message,
        tos: tos,
        slot: slot ? slot.id : null,
        marketing: marketing,
        adacMember: adacMember,
        startDate: toIsoString(slot.starts_at),
        endDate: toIsoString(slot.ends_at),
        calendarId: slot.calendar_id,
        acceptedAdditionalCharges: additionalCosts,
        premiumServices: premiumServices.map((premiumService) => premiumService.id),
        brand: brand?.id ? String(brand.id) : null,
        generation: generation?.id ? String(generation.id) : null,
        motorization: motorization ? motorization.id : null,
        gearbox: gearbox ? gearbox.id : null,
        body: body ? body.id : null,
        subRepairs: subRepairs ? subRepairs : null,
        registrationDate: initialRegistrationDate
          ? formatDate(initialRegistrationDate, "yyyy-MM-dd")
          : null,
        additionalPremiumServiceInformation:
          serviceWithAdditionalInformation && rentalCarSelection
            ? [
                {
                  id: serviceWithAdditionalInformation.id,
                  additionalInformation: {
                    rentalCar: rentalCarSelection,
                  },
                },
              ]
            : null,
        mustUseAuthorizedGarage: mustUseAuthorizedGarage,
        vin: vin ? vin : null,
        keyNumber: hsnTsn ? hsnTsn : null,
        reviewOptIn: reviewOptIn,
        ...(claimNumber && {
          insuranceClaim: { details: { damageNumber: claimNumber } },
        }),
        ...(garage.garageProperties?.length > 0 && {
          garageProperties: eligibleGarageProperties(
            garage.garageProperties,
            availableGarageProperties,
          ).map((p) => p.id),
        }),
        ...(contractNumber ? { contractNumber } : {}),
      },
      context: getContext(garage),
    });
  };

  const hasValidationError = (value: string): boolean => {
    return error.graphQLErrors.some(
      (error) =>
        error.message.includes(value) ||
        (error.extensions.validation &&
          `input.${value.replace("$", "")}` in error.extensions.validation),
    );
  };

  return {
    checkNumberPlate,
    organization,
    service,
    garage,
    premiumServices,
    slot,
    error,
    loading,
    inquiry: data ? data.createInquiry : null,
    validationErrors: {
      ...(error && hasValidationError("$firstName") && { firstName: true }),
      ...(error && hasValidationError("$lastName") && { lastName: true }),
      ...(error && hasValidationError("$email") && { email: true }),
      ...(error && hasValidationError("$phone") && { phone: true }),
      ...(error && hasValidationError("$numberPlate") && { numberPlate: true }),
      ...(error && hasValidationError("$mileage") && { mileage: true }),
      ...(error && hasValidationError("$message") && { message: true }),
      ...(error && hasValidationError("$tos") && { tos: true }),
    },
    submit,
  };
};
